import { useThree } from "@react-three/fiber";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { useCancelableDebouncedRequest } from "../../../../hooks/useCancelableDebouncedRequest ";
import { Store } from "../../../../store"; // adjust path
import { updateLayoutObjectApi } from "../../services/layoutObjectService";
import { pixelsToUnits } from "../../../../utils/helpers";

export default function InteractiveObject({
  object,
  editable = false,
  children,
  scaleFactor = 0.01,
  selectedElementRef,
  showObjectInfoPanel,
  setShowObjectInfoPanel,
  onOpenContextMenu,
  onSelect,
}) {
  const { state, dispatch } = useContext(Store);
  const { camera, gl } = useThree();

  // drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(null); // transient position while dragging

  // click-vs-drag detection
  const pointerStart = useRef({ x: 0, y: 0 });
  const movedSinceDown = useRef(false);
  const pointerIdRef = useRef(null);
  const clickThresholdPx = 6;

  // raycast helpers
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());
  const groundPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0));
  const intersection = useRef(new THREE.Vector3());
  const offset = useRef(new THREE.Vector3());

  // debounce save (you already had this)
  const savePositionToDB = useCancelableDebouncedRequest(250);

  // derive rendered position (store is source of truth)
  const storedPosition = [object?.position?.[0] || 0, 0.1, object?.position?.[2] || 0];
  const renderedPosition = dragPosition || storedPosition;

  // Sync when object prop changes (for example external updates)
  useEffect(() => {
    // if object moved externally, clear transient drag position
    setDragPosition(null);
  }, [object.position?.[0], object.position?.[1], object.position?.[2]]);

  // helper to update 2D coords from 3D (kept mostly as you had)
  const update2DCoordinatesFrom3D = useCallback(
    (newPosition3D) => {
      const centerX3D = newPosition3D[0];
      const centerZ3D = newPosition3D[2];

      const centerX2D = centerX3D / scaleFactor;
      const centerY2D = centerZ3D / scaleFactor;

      const width = Number(object.x2 - object.x1) || 0;
      const height = Number(object.y2 - object.y1) || 0;

      const newX1 = centerX2D - width / 2;
      const newY1 = centerY2D - height / 2;
      const newX2 = newX1 + width;
      const newY2 = newY1 + height;

      if (object.objectType === "wall") {
        const length = Math.sqrt((object.x2 - object.x1) ** 2 + (object.y2 - object.y1) ** 2);
        const rotation = object.rotation || 0;
        const dx = (Math.cos(rotation) * length) / 2;
        const dz = (Math.sin(rotation) * length) / 2;

        return {
          x: centerX2D,
          y: centerY2D,
          positionX: newX1,
          positionY: newY1,
          x1: centerX2D - dx,
          y1: centerY2D - dz,
          x2: centerX2D + dx,
          y2: centerY2D + dz,
        };
      }

      return {
        x: newX1,
        y: newY1,
        positionX: newX1,
        positionY: newY1,
        x1: newX1,
        y1: newY1,
        x2: newX2,
        y2: newY2,
      };
    },
    [object, scaleFactor],
  );

  // Save helper
  const save = async (id, payload) => {
    try {
      await savePositionToDB(updateLayoutObjectApi, { id, payload });
    } catch (err) {
      console.error("Save failed", err);
      throw err;
    }
  };

  // Handle click to show info panel
  const handleClick = (e) => {
    if (isDragging) return; // ignore clicks during drag

    e.stopPropagation();
    e.nativeEvent.stopPropagation?.();
    e.nativeEvent.stopImmediatePropagation?.();

    // shows the object info panel
    selectedElementRef.current = object;
    onSelect(object);
    setShowObjectInfoPanel(true);
  };

  // Pointer down
  const onPointerDown = (e) => {
    if (!editable) return;

    e.stopPropagation();
    e.nativeEvent.stopPropagation?.();
    e.nativeEvent.stopImmediatePropagation?.();

    // in the state set the selected element
    dispatch({
      type: "SET_SELECTED_ELEMENT",
      payload: object,
    });

    pointerIdRef.current = e.pointerId;
    setIsDragging(false);
    movedSinceDown.current = false;

    // store start client coords for click detection
    pointerStart.current = { x: e.clientX, y: e.clientY };

    // prepare raycasting and offset
    const rect = gl.domElement.getBoundingClientRect();
    mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.current.setFromCamera(mouse.current, camera);
    if (raycaster.current.ray.intersectPlane(groundPlane.current, intersection.current)) {
      offset.current.set(intersection.current.x - storedPosition[0], 0, intersection.current.z - storedPosition[2]);
    }

    gl.domElement.setPointerCapture(e.pointerId);
    gl.domElement.style.cursor = "grabbing";
  };

  // Pointer move
  const onPointerMove = (e) => {
    if (!editable || pointerIdRef.current !== e.pointerId) return;

    const rect = gl.domElement.getBoundingClientRect();

    // detect movement beyond threshold for click vs drag
    const dx = e.clientX - pointerStart.current.x;
    const dy = e.clientY - pointerStart.current.y;
    if (!movedSinceDown.current && Math.hypot(dx, dy) > clickThresholdPx) {
      movedSinceDown.current = true;
      setIsDragging(true);
    }

    // if dragging -> update transient position
    if (isDragging || movedSinceDown.current) {
      mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.current.setFromCamera(mouse.current, camera);

      if (raycaster.current.ray.intersectPlane(groundPlane.current, intersection.current)) {
        const newX = intersection.current.x - offset.current.x;
        const newZ = intersection.current.z - offset.current.z;
        const newY = 0.1;
        const newPos = [newX, newY, newZ];
        setDragPosition(newPos);
      }
    }
  };

  // Pointer up
  const onPointerUp = async (e) => {
    if (pointerIdRef.current !== e.pointerId) return;

    e.stopPropagation();
    e.nativeEvent.stopPropagation?.();

    gl.domElement.releasePointerCapture(e.pointerId);
    gl.domElement.style.cursor = "default";
    pointerIdRef.current = null;

    const wasMoved = movedSinceDown.current || isDragging;

    if (wasMoved && dragPosition) {
      // commit position to store and DB, but DO NOT open the info panel
      const updated2D = update2DCoordinatesFrom3D(dragPosition);

      const payload = {
        warehouseLayoutId: object.warehouseLayoutId,
        layoutObject: {
          positionX: Math.round(pixelsToUnits(updated2D.positionX, object.unitType)),
          positionY: Math.round(pixelsToUnits(updated2D.positionY, object.unitType)),
        },
      };

      // update store immediately
      dispatch({
        type: "UPDATE_ELEMENT",
        payload: {
          ...object,
          position: dragPosition,
          ...updated2D,
          hasElementChanges: true,
        },
      });
      if (showObjectInfoPanel) {
        selectedElementRef.current = {
          ...object,
          position: dragPosition,
          ...updated2D,
          hasElementChanges: true,
        };
        onSelect(selectedElementRef.current);
      }

      // debounce save (your hook) - we call savePositionToDB wrapper
      try {
        await save(object.id, payload);
      } catch (err) {
        // revert visually if save fails (optional)
        dispatch({
          type: "UPDATE_ELEMENT",
          payload: object, // revert to previous object snapshot
        });
        if (showObjectInfoPanel) {
          selectedElementRef.current = object;
          onSelect(selectedElementRef.current);
        }
      }
    } else {
      // treat as click (no meaningful move) -> SELECT element and show panel
      dispatch({
        type: "SET_SELECTED_ELEMENT",
        payload: object.id,
      });
      if (showObjectInfoPanel) {
        selectedElementRef.current = object;
        onSelect(selectedElementRef.current);
      }
    }

    // reset transient state
    setIsDragging(false);
    setDragPosition(null);
    movedSinceDown.current = false;
  };

  // Lost pointer capture (treat like up)
  const onLostPointerCapture = (e) => {
    if (pointerIdRef.current === e.pointerId) {
      pointerIdRef.current = null;
      setIsDragging(false);
      setDragPosition(null);
      gl.domElement.style.cursor = "default";
    }
  };

  // Optionally expose context menu or double click behaviour if needed
  const onPointerLeave = (e) => {
    // If pointer leaves canvas while dragging - commit as pointer up
    if (pointerIdRef.current === e.pointerId && isDragging) {
      onPointerUp(e);
    }
  };

  const groupRef = useRef();

  // pointer events from r3f give you event.point (world space)
  const handleContextMenu = (event) => {
    event.stopPropagation();

    // event.point is a THREE.Vector3 of the intersection point in world coords
    const worldPoint = event.point.clone();
    // optionally get screen coords:
    const { clientX, clientY } = event;

    // send object and world position to parent
    onOpenContextMenu &&
      onOpenContextMenu({
        object,
        worldPoint, // THREE.Vector3
        screen: { x: clientX, y: clientY },
      });
    onSelect(object);
  };

  return (
    <>
      <group
        ref={groupRef}
        position={renderedPosition}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onLostPointerCapture={onLostPointerCapture}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        {children}
      </group>
    </>
  );
}
